import React, { createContext, useContext, useState, useEffect } from 'react';
import secureStorage from '../utils/secureStorage';
import api from '../services/api'; // Use centralized API service

const AuthContext = createContext(null);
const AGENT_DASHBOARD_SYNC_ONCE_KEY = 'agent_dashboard_sync_once_done';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({
    user: null,
    loading: true,
    isInitialized: false,
    isAuthenticated: false
  });

  // Secure storage keys for cross-tab authentication
  const AUTH_USER_KEY = 'brokerage_app_user'; // Use same key as secureStorage
  
  // Initialize authentication state
  useEffect(() => {
    const hydrateUserFromCookieSession = async () => {
      try {
        const hasValidCookie = await secureStorage.verifyCookie();
        if (!hasValidCookie) {
          return null;
        }

        const profileResponse = await api.request('/auth/profile', {
          method: 'GET',
          skipAuthHandling: true,
        });

        if (!profileResponse.ok) {
          return null;
        }

        const responseData = await profileResponse.json().catch(() => null);
        const profile = (responseData && typeof responseData === 'object' && 'data' in responseData)
          ? responseData.data
          : responseData;

        if (!profile || !profile.email) {
          return null;
        }

        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(secureStorage.sanitizeUserData(profile)));
        return profile;
      } catch (error) {
        console.error('AuthContext cookie hydration failed:', error);
        return null;
      }
    };

    const initializeAuth = async () => {
      // console.log('🔄 AuthContext: SECURE initialization starting...');
      
      try {
        const user = await secureStorage.getUserData(); // Now async to check cookie validity
        const token = secureStorage.getToken();

        // console.log('🔍 AuthContext init - retrieved user:', user)
        // console.log('🔍 AuthContext init - token:', token);

        if (user && token) {
          // console.log('✅ Found authenticated user:', user.email);
          // console.log('🔍 AuthContext init - retrieved user:', user);
          // console.log('🔍 AuthContext init - user roles:', user?.roles);
          // console.log('🔍 AuthContext init - user primary_role:', user?.primary_role);

          setState({
            user: user,
            isAuthenticated: true,
            loading: false,
            isInitialized: true
          });
          // console.log('✅ User authenticated securely');
        } else {
          const hydratedUser = await hydrateUserFromCookieSession();

          if (hydratedUser) {
            setState({
              user: hydratedUser,
              isAuthenticated: true,
              loading: false,
              isInitialized: true
            });
            return;
          }

          console.log('❌ No authenticated session found');
          setState({
            user: null,
            isAuthenticated: false,
            loading: false,
            isInitialized: true
          });
        }
      } catch (error) {
        console.error('❌ Secure auth initialization error:', error);
        setState({
          user: null,
          isAuthenticated: false,
          loading: false,
          isInitialized: true
        });
      }
    };

    initializeAuth();
  }, []);

  // Re-validate the session when the user returns to the tab. A timer would
  // keep idle tabs refreshing forever and race other tabs for the shared
  // refresh cookie; normal API calls already self-heal on 401.
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const validateToken = async () => {
      const user = await secureStorage.getUserData(); // This will auto-clear if token/cookie is expired/invalid
      const token = secureStorage.getToken();
      
      if (!user || !token) {
        setState(prev => ({
          ...prev,
          user: null,
          isAuthenticated: false
        }));
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') validateToken();
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [state.isAuthenticated]);

  // Listen for storage changes across tabs (secure cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e) => {
      // console.log('🔄 Secure storage change detected:', e.key);
      
      if (e.key === AUTH_USER_KEY) {
        try {
          const newUserData = e.newValue ? JSON.parse(e.newValue) : null;
          // console.log('👤 User data changed in another tab:', newUserData?.email);
          
          if (newUserData) {
            // For cross-tab sync, use basic token check (can't await cookie verification here)
            const token = secureStorage.getToken();
            if (token) {
              setState(prev => ({
                ...prev,
                user: newUserData,
                isAuthenticated: true
              }));
              // console.log('✅ Synced SECURE LOGIN from another tab');
            } else {
              // console.log('⚠️ User data found but no valid token, clearing state');
              setState(prev => ({
                ...prev,
                user: null,
                isAuthenticated: false
              }));
            }
          } else {
            setState(prev => ({
              ...prev,
              user: null,
              isAuthenticated: false
            }));
            // console.log('❌ Synced SECURE LOGOUT from another tab');
          }
        } catch (error) {
          console.error('Error parsing secure storage change:', error);
        }
      }
    };

    // console.log('👂 Listening for secure storage changes across tabs');
    window.addEventListener('storage', handleStorageChange);
    return () => {
      // console.log('🔇 Stopped listening for storage changes');
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [AUTH_USER_KEY]);

  const login = async (user, token) => {
    try {
      // console.log('� Secure login for:', user.email);
      // console.log('🔍 AuthContext login - received user:', user);
      // console.log('🔍 AuthContext login - user roles:', user?.roles);
      // console.log('🔍 AuthContext login - user primary_role:', user?.primary_role);
      
      // Use secure storage (httpOnly cookies + localStorage fallback)
      await secureStorage.setAuthData(token, user);

      // Ensure agent dashboard auto-sync can run once after each fresh login.
      sessionStorage.removeItem(AGENT_DASHBOARD_SYNC_ONCE_KEY);
      
      setState({
        user: user,
        isAuthenticated: true,
        loading: false,
        isInitialized: true
      });
      
      // console.log('✅ Secure login complete - cross-tab sync enabled');
    } catch (error) {
      console.error('❌ Secure login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('🚪 Secure logout');
      
      // Clear all secure auth data (cookies + localStorage)
      await secureStorage.clearAuthData();

      // Reset one-time dashboard sync marker for the next login.
      sessionStorage.removeItem(AGENT_DASHBOARD_SYNC_ONCE_KEY);
      
      // console.log('✅ Secure logout complete - cleared from all tabs');
    } catch (error) {
      console.error('❌ Secure logout error:', error);
    } finally {
      // ALWAYS reset auth state, even if clearAuthData fails
      setState({
        user: null,
        isAuthenticated: false,
        loading: false,
        isInitialized: true
      });
    }
  };

  const updateUser = async (userData) => {
    try {
      // console.log('🔄 Secure user update');
      // console.log('🔍 AuthContext updateUser - new user data:', userData);
      
      // Update user data in secure storage (localStorage for user data is safe)
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(secureStorage.sanitizeUserData(userData)));
      
      setState(prev => ({
        ...prev,
        user: userData
      }));
      
      console.log('✅ User updated securely');
    } catch (error) {
      console.error('❌ Update error:', error);
    }
  };

  const resetPassword = async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return true; // Success
    } catch (error) {
      console.error('❌ Reset password error:', error);
      throw error;
    }
  };

  // Manual secure recheck function for debugging
  const recheckAuth = () => {
    // console.log('🔄 Manual secure recheck triggered');
    try {
      const user = secureStorage.getUserData();
      const token = secureStorage.getToken();
      
      if (user && token) {
        setState(prev => ({
          ...prev,
          user: user,
          isAuthenticated: true
        }));
        // console.log('✅ Secure recheck: authenticated as', user.email);
        // console.log('🔒 Token type:', token === 'cookie-auth' ? 'httpOnly cookie' : 'localStorage fallback');
      } else {
        setState(prev => ({
          ...prev,
          user: null,
          isAuthenticated: false
        }));
        console.log('❌ Secure recheck: not authenticated');
      }
    } catch (error) {
      console.error('❌ Secure recheck error:', error);
    }
  };

  const value = {
    state,
    login,
    logout,
    updateUser,
    resetPassword,
    recheckAuth,
    // Compatibility props
    currentUser: state.user,
    isLoading: state.loading,
    isAuthenticated: state.isAuthenticated
  };

  // Make available globally for debugging
  React.useEffect(() => {
    window.authContext = value;
    // console.log('🌐 AuthContext available globally as window.authContext');
  }, [value]);

  if (!state.isInitialized) {
    return <div></div>;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;